<template>
  <a-modal
    :open="open"
    title="Generar audios"
    centered
    ok-text="Generar audios"
    cancel-text="Cancelar"
    :confirm-loading="generatingAudios"
    :ok-button-props="{ disabled: disableGenerateButton }"
    @update:open="handleOpenChange"
    @ok="handleGenerateTtsAudios"
  >
    <p>
      Para lanzar tu estrategia, se generará un audio por cada registro en tu
      plantilla y se realizará el cobro correspondiente desde tu wallet de Text To
      Speech.
    </p>

    <a-spin :spinning="loadingGenerationInfo">
      <div class="tts-generation-payment">
        <h4>Información de pago</h4>
        <div>
          <span>Cantidad de registros</span>
          <span>{{ formattedRecords }}</span>
        </div>
        <div>
          <span>Costo por audio</span>
          <span>{{ formattedAudioCost }}</span>
        </div>
        <div class="tts-generation-payment__total">
          <span>Total por estrategia</span>
          <span class="tts-generation-payment__total--cost">{{ formattedTotalCost }}</span>
        </div>
        <p
          v-if="generationInfo && billingType == BillingType.Prepaid"
          class="tts-generation-payment__balance"
          :class="{ 'tts-generation-payment__balance--error': hasInsufficientBalance }"
        >
          {{ balanceStatusText }}
        </p>
        <PayUPaymentButton
          v-if="hasInsufficientBalance"
          :amount="rechargeAmount"
          :min-amount="rechargeAmount"
          :type="ProductType.TextToSpeech"
          title="Recarga tu saldo de Text To Speech"
          show-amount-input
          @update="fetchGenerationInfo"
          @success="fetchGenerationInfo"
        />
      </div>
    </a-spin>

    <div class="tts-generation-start">
      <h4>Configura el inicio de la estrategia</h4>
      <a-radio-group v-model:value="startMode">
        <a-radio :value="StrategyTTSStartMode.Manual">Manualmente</a-radio>
        <a-radio :value="StrategyTTSStartMode.Automatic">Automáticamente</a-radio>
      </a-radio-group>
      <p v-if="startMode === StrategyTTSStartMode.Automatic">
        La estrategia iniciará cuando los audios terminen de generarse.
      </p>
    </div>

    <a-alert
      v-if="isIvrReturn"
      class="tts-generation-ivr-alert"
      type="warning"
      show-icon
    >
      <template #message>
        <strong>Recuerda:</strong>
        Las estrategias de tipo IVR con retorno deben tener usuarios asignados
        para poder iniciar.
        <StrategyAgentsModal type-link :strategy="strategy" />
      </template>
    </a-alert>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import StrategyAgentsModal from "@/app/strategies/components/modals/StrategyAgentsModal.vue";
import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import { BillingType, ProductType } from "@/@types/settings/billing";
import { StrategyTTSStartMode } from "../../utils/modalStrategy";
import { fetchWhatsappBalance } from "@/app/settings/services";

defineOptions({
  name: "StrategyGenerateTtsAudiosModal",
});

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  strategy: {
    type: Object,
    required: true,
  },
  isIvrReturn: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:open", "generated"]);

const store = useStore();

const MIN_TTS_RECHARGE_AMOUNT = 5;

const generatingAudios = ref(false);
const loadingGenerationInfo = ref(false);
const generationInfo = ref(null);
const startMode = ref(StrategyTTSStartMode.Automatic);
const billingType = ref();

const recordsCount = computed(() => {
  return (
    generationInfo.value?.total_records ??
    props.strategy?.total_phones ??
    props.strategy?.records_count ??
    0
  );
});

const audioCost = computed(() => {
  if (generationInfo.value) return Number(generationInfo.value.price_per_audio || 0);

  return Number(
    props.strategy?.tts_audio_cost ||
      props.strategy?.cost_per_audio ||
      props.strategy?.audio_cost ||
      0
  );
});

const totalCost = computed(() => {
  if (generationInfo.value) return Number(generationInfo.value.total_cost || 0);

  return Number(props.strategy?.tts_total_cost || audioCost.value * recordsCount.value);
});

const ttsBalance = computed(() => Number(generationInfo.value?.tts_balance || 0));

const balanceDifference = computed(() => ttsBalance.value - totalCost.value);

const hasInsufficientBalance = computed(() => {
  return Boolean(generationInfo.value) && balanceDifference.value < 0 && billingType.value == BillingType.Prepaid;
});

const rechargeAmount = computed(() => {
  const amount = Math.max(Math.abs(balanceDifference.value), MIN_TTS_RECHARGE_AMOUNT);
  return Math.ceil(amount * 100) / 100;
});

const balanceStatusText = computed(() => {
  if (hasInsufficientBalance.value) {
    return `Saldo insuficiente. Recargar mínimo ${formatCurrency(
      Math.abs(balanceDifference.value)
    )}`;
  }

  return `Te quedará ${formatCurrency(balanceDifference.value)} de saldo disponible`;
});

const disableGenerateButton = computed(() => {
  return loadingGenerationInfo.value || hasInsufficientBalance.value || !generationInfo.value;
});

const formattedRecords = computed(() => {
  return `${new Intl.NumberFormat("en-US").format(recordsCount.value)} registros`;
});

const formattedAudioCost = computed(() => {
  return formatCurrency(audioCost.value, audioCost.value < 0.01 ? 6 : 2);
});

const formattedTotalCost = computed(() => formatCurrency(totalCost.value));

function formatCurrency(value, decimals) {
  const currencyDecimals = decimals ?? getCurrencyDecimals(value);
  return `$${Number(value || 0).toFixed(currencyDecimals)}`;
}

function getCurrencyDecimals(value) {
  const absoluteValue = Math.abs(Number(value || 0));
  return absoluteValue > 0 && absoluteValue < 0.01 ? 6 : 2;
}

async function fetchGenerationInfo() {
  if (!props.strategy?.id) return;

  try {
    loadingGenerationInfo.value = true;
    generationInfo.value = await store.dispatch(
      "fetchTtsAudioGenerationInfo",
      props.strategy.id
    );
    // balance
    const response = await fetchWhatsappBalance('tts');
    billingType.value = response.type_billing;
  } catch (error) {
    const errorMessage =
      error?.getErrorMessage?.() || "No se pudo obtener la información de pago";
    message.error(errorMessage);
    emit("update:open", false);
  } finally {
    loadingGenerationInfo.value = false;
  }
}

function handleOpenChange(open) {
  emit("update:open", open);
}

async function handleGenerateTtsAudios() {
  if (disableGenerateButton.value) return;

  try {
    generatingAudios.value = true;
    message.info({
      content: () => "Estamos generando los audios para tus registros. Te notificaremos al finalizar.",
      class: 'tts-generation-message',
    });
    await store.dispatch("generateMultipleTtsAudios", {
      strategyId: props.strategy.id,
      startMode: startMode.value,
    });
    emit("update:open", false);
    emit("generated");
    // message.success("La generación de audios inició correctamente");
  } catch (error) {
    const errorMessage =
      error?.getErrorMessage?.() || "No se pudo iniciar la generación de audios";
    message.error(errorMessage);
  } finally {
    generatingAudios.value = false;
  }
}

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) return;
    await fetchGenerationInfo();
  },
  { immediate: true }
);
</script>

<style lang="sass" scoped>
.tts-generation-payment
  border: 1px solid $blue-4
  background: $blue-1
  border-radius: 8px
  padding: 16px
  margin: 18px 0

  h4
    margin-bottom: 10px
    font-weight: 600

  div
    display: flex
    justify-content: space-between
    gap: 16px
    margin-bottom: 4px
    color: $gray-8

  &__total
    align-items: center
    margin-top: 10px
    color: $gray-9
    font-weight: 600
    &--cost
      font-size: 20px

  &__balance
    margin: 6px 0 10px
    color: $gray-7
    font-size: $font-small
    text-align: right

    &--error
      color: $red-6

.tts-generation-start
  border: 1px solid $gray-5
  border-radius: 4px
  padding: 16px

  h4
    margin-bottom: 14px
    font-weight: 600

  p
    color: $gray-8
    margin: 14px 0 0

.tts-generation-ivr-alert
  margin-top: 14px

:global(.tts-generation-message .ant-message-notice-content)
  background-color: #e6f4ff
  border: 1px solid #91caff
</style>
